/**
 * WorkspaceDetailPage component
 * Displays workspace details with Apps and Users tabs
 */

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Spinner,
} from '@fluentui/react-components';
import { PageLayout } from '../components/layouts';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, Permission } from '../types/auth';
import { useWorkspace } from '../hooks/useWorkspace';
import { AppsTabContent } from '../components/Workspace/AppsTabContent';
import { OverviewTabContent } from '../components/Workspace/OverviewTabContent';
import { UserManagementPage } from './UserManagementPage';

const useStyles = makeStyles({
  centerContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorPaletteRedForeground1,
  },
});

type TabValue = 'overview' | 'apps' | 'users';

export function WorkspaceDetailPage() {
  const styles = useStyles();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // React Query hook for workspace data
  const {
    workspace,
    isLoading,
    error,
  } = useWorkspace(workspaceId);

  // Get active tab from URL or default to 'overview'
  const tabParam = searchParams.get('tab');
  const activeTab: TabValue =
    tabParam === 'apps' ? 'apps' : tabParam === 'users' ? 'users' : 'overview';

  // Check if user can see Users tab
  const canViewUsers =
    workspace?.hasUserAccess ||
    hasPermission(user, Permission.MANAGE_TENANT_USERS);

  // Build tabs array based on permissions
  const tabs = useMemo(() => {
    const tabList = [
      { value: 'overview', label: 'Overview' },
      { value: 'apps', label: 'Apps' },
    ];
    if (canViewUsers) {
      tabList.push({ value: 'users', label: 'Users' });
    }
    return tabList;
  }, [canViewUsers]);

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  if (isLoading) {
    return (
      <div className={styles.centerContent}>
        <Spinner label="Loading workspace..." />
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className={styles.errorState}>
        <Text size={400}>Error loading workspace</Text>
        <Text>{error?.message || 'Workspace not found'}</Text>
      </div>
    );
  }

  return (
    <PageLayout
      title={workspace.name}
      subtitle={workspace.notes ?? undefined}
      tabs={tabs}
      selectedTab={activeTab}
      onTabChange={handleTabChange}
      testId="workspace-detail-page"
    >
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTabContent
          workspaceId={workspace.id}
          workspaceName={workspace.name}
          domain={workspace.domain}
        />
      )}

      {activeTab === 'apps' && (
        <AppsTabContent
          workspaceId={workspace.id}
          workspaceName={workspace.name}
          domain={workspace.domain}
        />
      )}

      {activeTab === 'users' && canViewUsers && (
        <UserManagementPage
          embedded={true}
          tenantId={workspace.id}
          columnView="workspace"
        />
      )}
    </PageLayout>
  );
}
